import { useForm } from "react-hook-form";
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
} from "app/services/api/usersApi";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { CreateUserRequest } from "app/services/api/types/residents/requests/CreateUserRequest";
import { ToastModel } from "ui/types/ToastModel";
import { AuthUserModel } from "app/services/api/types/authorisation/models/AuthUserModel";
import { EditUserRequest } from "app/services/api/types/users/requests/EditUserRequest";

export const useHandleUserForm = (
  onSuccess?: () => void,
  initialUser?: AuthUserModel | null,
) => {
  const { showNewToast } = useDisplayToast();
  const [triggerCreateUser, { isLoading: isCreateLoading }] =
    useCreateUserMutation();
  const [triggerUpdateUser, { isLoading: isUpdateLoading }] =
    useUpdateUserMutation();
  const [triggerDeleteUser, { isLoading: isDeleting }] =
    useDeleteUserMutation();

  const { control, handleSubmit } = useForm<CreateUserRequest>({
    defaultValues: {
      first_name: initialUser?.first_name || "",
      last_name: initialUser?.last_name || "",
      email: initialUser?.email || "",
      roles: initialUser?.roles?.map((r) => r.id) ?? [],
      locations: initialUser?.locations?.map((l) => l.id) ?? [],
    },
  });

  const handleCreateUser = async (formData: CreateUserRequest) => {
    try {
      await triggerCreateUser(formData).unwrap();
      handleToast({
        id: "user-created",
        title: "New user has been created!",
        message: `${formData.first_name} ${formData.last_name} with email ${formData.email} has been added. An email will be sent to them to set their preferred password.`,
        type: "success",
        duration: 5000,
      });

      onSuccess?.();
    } catch (e) {
      handleToast({
        id: "user-creation-error",
        title: "Something went wrong",
        message: e?.data?.message || "An unexpected error occurred.",
        type: "error",
      });
      console.error("Error:", e);
    }
  };

  const handleUpdateUser = async (
    formData: EditUserRequest,
    userId: number,
  ) => {
    try {
      await triggerUpdateUser({ user: userId, body: formData }).unwrap();
      onSuccess?.();

      handleToast({
        id: "user-updated",
        title: "User has been updated!",
        message:
          "The user was successfully updated to the system. Please verify their details from the table below.",
        type: "success",
      });
    } catch (e) {
      handleToast({
        id: "user-update-error",
        title: "Something went wrong",
        message: e?.data?.message || "An unexpected error occurred.",
        type: "error",
      });
      console.error("Error updating role:", e);
    }
  };

  const handleToast = (toast: ToastModel) => {
    showNewToast(toast);
  };

  const handleUserSubmission = async (
    formData: CreateUserRequest,
    initialUser?: AuthUserModel,
  ) => {
    if (initialUser?.id) {
      await handleUpdateUser(formData, initialUser.id);
    } else {
      await handleCreateUser(formData);
    }
  };

  const submitForm = handleSubmit(async (formData: CreateUserRequest) => {
    handleUserSubmission(formData, initialUser ?? undefined)
      .then(onSuccess)
      .catch(() => {});
  });

  const handleDeleteUser = async ({ id }: Pick<AuthUserModel, "id">) => {
    try {
      await triggerDeleteUser({ user: id });
      onSuccess?.();
      showNewToast({
        id: `user-deleted-${id}`,
        title: "User deleted successfully",
        message: "The user has been deleted successfully",
        type: "success",
      });
    } catch (e) {
      handleToast({
        id: "role-deletion-error",
        title: "Something went wrong",
        message: e?.data?.message || "An unexpected error occurred.",
        type: "error",
      });
    }
  };

  return {
    control,
    isLoadingAction: isCreateLoading || isUpdateLoading || isDeleting,
    submitForm,
    handleDeleteUser,
  };
};
